/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx } from '@compiled/react';
import type { TableState } from '@tanstack/react-table';

import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { type CharlieTableProps } from '../../charlie-table.types';

import { type ColumnMetadata, getIconPropsForAction, reorderColumn } from './more-actions-utils';

export const DEFAULT_COLUMN_ACTIONS: NonNullable<CharlieTableProps<any>['columnActions']> = {
	pinToLeft: false,
	unpin: true,
	moveToFront: false,
	moveToBack: false,
	hiding: false,
	moveRight: true,
	moveLeft: true,
	sortAscending: true,
	sortDescending: true,
	clearSorting: true,
};

export interface TableActions {
	toggleSorting: (desc: boolean) => void;
	clearSorting: () => void;
	setColumnOrder: (order: string[]) => void;
	setColumnPinning: (pinning: { left?: string[]; right?: string[] }) => void;
	setColumnVisibility: (visibility: Record<string, boolean>) => void;
}

interface MoreActionsMenuProps<T extends object> {
	/**
	 * The column to display the more actions menu for.
	 */
	column: ColumnMetadata;
	/**
	 * The current state of the table.
	 */
	tableState: Pick<TableState, 'columnOrder' | 'columnPinning' | 'columnVisibility'>;
	/**
	 * State management functions for the table.
	 */
	tableActions: TableActions;
	labels: NonNullable<CharlieTableProps<T>['labels']>;
	interactionName?: string;
	closeMenu?: () => void;
	/**
	 * A set of the allowed column moves, IE ['left', 'right']. This is used internally to determine what actions to show in the menu.
	 */
	allowedColumnMoves?: Set<'left' | 'right'> | false;
	/**
	 * The available actions for the column. This is source of truth for what actions are available for the column.
	 */
	columnActions: NonNullable<CharlieTableProps<T>['columnActions']>;
}

export const MoreActionsMenu = <T extends object>({
	column,
	closeMenu,
	labels,
	tableState,
	tableActions,
	allowedColumnMoves,
	columnActions,
	interactionName,
}: MoreActionsMenuProps<T>): React.JSX.Element => {
	const canColumnSort = column.canSort;
	const isPinned = column.isPinned;
	const isLeftPinned = tableState.columnPinning?.left?.includes(column.id);
	const isRightPinned = tableState.columnPinning?.right?.includes(column.id);

	const showSortAscending = columnActions.sortAscending && canColumnSort;
	const showSortDescending = columnActions.sortDescending && canColumnSort;
	const showClearSorting = columnActions.clearSorting && canColumnSort && column.isSorted;
	const showMoveLeft =
		columnActions.moveLeft && allowedColumnMoves && allowedColumnMoves.has('left');
	const showMoveRight =
		columnActions.moveRight && allowedColumnMoves && allowedColumnMoves.has('right');
	const showMoveToFront =
		columnActions.moveToFront && !isPinned && allowedColumnMoves && allowedColumnMoves.has('left');
	const showMoveToBack =
		columnActions.moveToBack && !isPinned && allowedColumnMoves && allowedColumnMoves.has('right');
	const showPinToLeftActions = columnActions.pinToLeft && !isRightPinned;

	const showUnpinActions = columnActions.unpin && isPinned;
	const showPinningActions = showPinToLeftActions || showUnpinActions;
	const showHiding = columnActions.hiding && column.canHide;

	// TODO: Column order state defaults to [], so we need to do some checks to avoid weird behavior.
	const hasSortingActions = showSortAscending || showSortDescending || showClearSorting;
	const hasMoveActions = showMoveLeft || showMoveRight || showMoveToFront || showMoveToBack;

	return (
		<MenuGroup key="sort-actions">
			{hasSortingActions && (
				<Section titleId="sort-group-label">
					<VisuallyHidden id="sort-group-label">{labels.sortGroup}</VisuallyHidden>
					{showSortAscending && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'sortAscending' })}
							interactionName={`${interactionName}-sort-ascending`}
							onClick={() => {
								tableActions.toggleSorting(false);
								closeMenu?.();
							}}
						>
							{labels.sortAscending}
						</ButtonItem>
					)}
					{showSortDescending && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'sortDescending' })}
							interactionName={`${interactionName}-sort-descending`}
							onClick={() => {
								tableActions.toggleSorting(true);
								closeMenu?.();
							}}
						>
							{labels.sortDescending}
						</ButtonItem>
					)}
					{showClearSorting && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'clearSorting' })}
							interactionName={`${interactionName}-clear-sorting`}
							onClick={() => {
								tableActions.clearSorting();
								closeMenu?.();
							}}
						>
							{labels.clearSorting}
						</ButtonItem>
					)}
				</Section>
			)}
			{hasMoveActions && (
				<Section titleId="move-column-group-label" hasSeparator={!!hasSortingActions}>
					<VisuallyHidden id="move-column-group-label">{labels.moveColumnGroup}</VisuallyHidden>
					{showMoveLeft && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'moveLeft' })}
							interactionName={`${interactionName}-move-column-left`}
							onClick={() => {
								const result = reorderColumn({
									movingColumnId: column.id,
									targetDistance: -1,
									columnOrder: tableState.columnOrder,
									columnVisibility: tableState.columnVisibility,
									columnPinning: tableState.columnPinning,
								});

								tableActions.setColumnOrder(result.columnOrder);
								if (result.columnPinning) {
									tableActions.setColumnPinning(result.columnPinning);
								}
								closeMenu?.();
							}}
						>
							{labels.moveColumnLeft}
						</ButtonItem>
					)}
					{showMoveRight && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'moveRight' })}
							interactionName={`${interactionName}-move-column-right`}
							onClick={() => {
								const result = reorderColumn({
									movingColumnId: column.id,
									targetDistance: 1,
									columnOrder: tableState.columnOrder,
									columnVisibility: tableState.columnVisibility,
									columnPinning: tableState.columnPinning,
								});

								tableActions.setColumnOrder(result.columnOrder);
								if (result.columnPinning) {
									tableActions.setColumnPinning(result.columnPinning);
								}
								closeMenu?.();
							}}
						>
							{labels.moveColumnRight}
						</ButtonItem>
					)}
					{showMoveToFront && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'moveToFront' })}
							interactionName={`${interactionName}-move-column-first`}
							onClick={() => {
								const result = reorderColumn({
									movingColumnId: column.id,
									targetDistance: 'first',
									columnOrder: tableState.columnOrder,
									columnVisibility: tableState.columnVisibility,
									columnPinning: tableState.columnPinning,
								});
								tableActions.setColumnOrder(result.columnOrder);
								if (result.columnPinning) {
									tableActions.setColumnPinning(result.columnPinning);
								}
								closeMenu?.();
							}}
						>
							{labels.moveColumnFirst}
						</ButtonItem>
					)}
					{showMoveToBack && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'moveToBack' })}
							interactionName={`${interactionName}-move-column-last`}
							onClick={() => {
								const result = reorderColumn({
									movingColumnId: column.id,
									targetDistance: 'last',
									columnOrder: tableState.columnOrder,
									columnVisibility: tableState.columnVisibility,
									columnPinning: tableState.columnPinning,
								});
								tableActions.setColumnOrder(result.columnOrder);
								if (result.columnPinning) {
									tableActions.setColumnPinning(result.columnPinning);
								}
								closeMenu?.();
							}}
						>
							{labels.moveColumnLast}
						</ButtonItem>
					)}
				</Section>
			)}
			{(showPinningActions || showHiding) && (
				<Section
					titleId="column-visibility-group-label"
					hasSeparator={!!(hasSortingActions || hasMoveActions)}
				>
					<VisuallyHidden id="column-visibility-group-label">
						{labels.columnVisibilityGroup}
					</VisuallyHidden>
					{showPinningActions && isLeftPinned && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'unpin' })}
							interactionName={`${interactionName}-unpin-column`}
							onClick={() => {
								tableActions.setColumnPinning({
									...tableState.columnPinning,
									left: tableState.columnPinning?.left?.filter(
										(columnId) => columnId !== column.id,
									),
								});
								closeMenu?.();
							}}
						>
							{labels.unpinColumn}
						</ButtonItem>
					)}
					{showPinToLeftActions && !isLeftPinned && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'pinToLeft' })}
							interactionName={`${interactionName}-pin-column`}
							onClick={() => {
								tableActions.setColumnPinning({
									...tableState.columnPinning,
									left: [...(tableState.columnPinning?.left || []), column.id],
								});
								closeMenu?.();
							}}
						>
							{labels.pinColumn}
						</ButtonItem>
					)}
					{showHiding && (
						<ButtonItem
							{...getIconPropsForAction({ columnActions, action: 'hiding' })}
							interactionName={`${interactionName}-hide-column`}
							onClick={() => {
								tableActions.setColumnVisibility({
									...tableState.columnVisibility,
									[column.id]: false,
								});
								closeMenu?.();
							}}
						>
							{labels.hideColumn}
						</ButtonItem>
					)}
				</Section>
			)}
		</MenuGroup>
	);
};
